import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { ColumnsConfig, TableConfig, TableData } from 'sct-custom-table/sct-table/projects/sct-table/src/lib/custom-table-interface';
import { SideMenuService } from 'src/app/shared/side-menu/side-menu.service';
import { SitesService } from 'src/app/sites/sites.service';
import { SiteDashboardService } from '../../site-dashboard.service';
import { CommonService } from 'src/app/shared/services/common.service';
import { DecimalPipe } from '@angular/common';
import { DeviceFactory } from '../../device/device-factory/device-factory';
import { Subscription } from 'rxjs';

@Component({
	selector: 'app-monthly-quarterly-charger-usage-report',
	templateUrl: './monthly-quarterly-charger-usage-report.component.html',
	styleUrls: ['./monthly-quarterly-charger-usage-report.component.css']
})
export class MonthlyQuarterlyChargerUsageReportComponent implements OnInit, OnDestroy {
	siteId = 0;
	allYearsId = 9999;
	yearList: {label: number, id: number}[] = [];
	selectedYear: number = new Date().getFullYear();
	usageData = {};
	siteInstallationDate = null;

	periodTypes = [
		{label: this.translate.instant('g.monthly'), id: 'monthly'},
		{label: this.translate.instant('g.quarterly'), id: 'quarterly'},
	];

	selectedPeriodType = 'monthly';

	columnConfig: ColumnsConfig[] = [
		{key: 'period', name: this.translate.instant('connectivity_verification.month'), type: 'string'},
		{key: 'chargers_count', name: this.translate.instant('fleet_reports.chargers_count'), type: 'number'},
		{key: 'used_chargers_count', name: this.translate.instant('fleet_reports.used_chargers_count'), type: 'number'},
		{key: 'usage_score', name: this.translate.instant('fleet_reports.utilization_score_percent'), type: 'number', hasTooltip: this.translate.instant('tooltip.chargLinkDevice.site_monthly_usage_utilization_score_column')},
		{key: 'total_daily_charge_hour', name: this.translate.instant('fleet_reports.total_charge_hours'), type: 'number'},
		{key: 'total_daily_idle_hour', name: this.translate.instant('fleet_reports.total_idle_hours'), type: 'number'},
		{key: 'total_daily_off_hour', name: this.translate.instant('fleet_reports.total_off_hours'), type: 'number'},
		{key: 'total_daily_ah', name: this.translate.instant('fleet_reports.total_charge_ah'), type: 'number'},
		{key: 'total_daily_kwh', name: this.translate.instant('fleet_reports.total_charge_kwh'), type: 'number'},
	];

	tableData: TableData[] = [];
	tableConfig: TableConfig = {
		hasExport: true,
		hasPagination: true,
		pageSize: 100,
		fitScreen: true,
		hideNoData: true,
		draggable: true,
		fileName: this.translate.instant('fleet_reports.monthly_quarterly_charger_usage_report'),
		csvFileHeaderInfo: []
	};

	siteSubscription: Subscription = new Subscription();

	constructor(
		private sitesService: SitesService,
		private route: ActivatedRoute,
		private translate: TranslateService,
		private sideMenuService: SideMenuService,
		private siteDashboardService: SiteDashboardService,
		private commonService: CommonService,
	) {}

	ngOnInit() {
		this.siteId = this.route.parent.snapshot.params.siteId || 0;
		this.siteSubscription = this.sideMenuService.currentSite.subscribe((siteInfo:any) => {
			if (!siteInfo || !siteInfo.installation_date)
				return;

			this.tableConfig.csvFileHeaderInfo = this.commonService.getCsvFileHeadersInfo({
				customerName: siteInfo.customer_name,
				siteName: siteInfo.name,
				includeSpacing: false
			});
			this.siteInstallationDate = moment.utc(siteInfo.installation_date).startOf('day').toDate();
			this.getMonthlyQuarterlyChargerUsageReport();

			const {yearList, currentYear} =  this.siteDashboardService.prepareYearsList(this.yearList, this.siteInstallationDate);
			this.yearList = yearList;
			this.yearList.unshift({label: this.translate.instant('site_performance.truck_year_all'), id: this.allYearsId});
			this.selectedYear = currentYear;
		});
	}

	isAllYearsSelected() {
		return this.selectedYear === this.allYearsId;
	}

	selectYear() {
		this.setPeriodName();
		this.getMonthlyQuarterlyChargerUsageReport();
	}

	setPeriodName() {
		this.columnConfig[0].name = this.selectedPeriodType === 'monthly' ? this.translate.instant('connectivity_verification.month') : this.translate.instant('site.quarter');
		if (this.isAllYearsSelected())
			this.columnConfig[0].name = this.translate.instant('g.year');
	}

	changePeriodType() {
		this.setPeriodName();
		let data = this.isAllYearsSelected() ? this.usageData?.[this.selectedPeriodType] || {} : this.usageData?.[this.selectedPeriodType]?.[this.selectedYear] || {};
		this.updateTableData(data);
	}

	getMonthlyQuarterlyChargerUsageReport() {
		this.sitesService.getMonthlyQuarterlyUsageReport(this.siteId, this.selectedYear, [DeviceFactory.types.charglink, DeviceFactory.types.brm], { getAllYears: this.isAllYearsSelected() }).subscribe((res: any) => {
			if (!res.quarterly && !res.monthly)
				return;

			this.usageData = res;
			let data = this.isAllYearsSelected() ? res?.yearly || {} : res?.[this.selectedPeriodType]?.[this.selectedYear] || {};
			this.updateTableData(data);
		});
	}

	updateTableData(data) {
		const tableData = [];

		for (const period in data) {
			if (data.hasOwnProperty(period)) {
				let periodValue = this.selectedPeriodType === 'monthly' ? this.commonService.monthsText[+data[period].month - 1] : data[period].quarter;
				if (this.isAllYearsSelected())
					periodValue = data[period].year;

				tableData.push({
					month: +data[period].month,
					period: {value: periodValue},
					chargers_count: {value: data[period].chargers_count},
					used_chargers_count: {value: data[period].used_chargers_count},
					usage_score: {value: this.siteDashboardService.calculateUsageScore(data[period].chargers_count, data[period].used_chargers_count)},
					total_daily_charge_hour: {value: Math.round((data[period].total_charge_second || 0) / 3600).toLocaleString()},
					total_daily_idle_hour: { value: Math.round((data[period].total_idle_second || 0) / 3600).toLocaleString() },
					total_daily_off_hour: { value: Math.round((data[period].total_off_time_second || 0) / 3600).toLocaleString() },
					total_daily_ah: {value: Math.round((data[period].total_as || 0) / 3600).toLocaleString()},
					total_daily_kwh: {value: Math.round((data[period].total_ws || 0) / (3600 * 1000)).toLocaleString()},
				});
			}
		}

		// sort by period
		tableData.sort((a, b) => { return a.month - b.month; });
		this.tableData = tableData;
	}

	ngOnDestroy() {
		this.siteSubscription.unsubscribe();
	}
}
